"use client";

import React, { useState, useCallback } from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";

interface FacetValue {
  value: string;
  count: number;
}

interface SearchFiltersProps {
  facets: {
    types: FacetValue[];
    status: FacetValue[];
    priority: FacetValue[];
    tags: FacetValue[];
  };
  selectedFilters: {
    types?: string[];
    status?: string[];
    priority?: string[];
    tags?: string[];
  };
  onFilterChange: (filters: any) => void;
  onClearFilters: () => void;
}

export function SearchFilters({
  facets,
  selectedFilters,
  onFilterChange,
  onClearFilters,
}: SearchFiltersProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    types: true,
    status: true,
    priority: false,
    tags: false,
  });

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleFilterToggle = useCallback((filterType: string, value: string) => {
    const currentValues = selectedFilters[filterType as keyof typeof selectedFilters] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    onFilterChange({
      ...selectedFilters,
      [filterType]: newValues.length > 0 ? newValues : undefined,
    });
  }, [selectedFilters, onFilterChange]);

  const hasActiveFilters = Object.values(selectedFilters).some(
    v => Array.isArray(v) && v.length > 0
  );

  const renderFilterSection = (
    title: string,
    sectionKey: string,
    values: FacetValue[]
  ) => {
    if (values.length === 0) return null;

    const isExpanded = expandedSections[sectionKey];
    const selectedValues = selectedFilters[sectionKey as keyof typeof selectedFilters] || [];

    return (
      <div key={sectionKey} className="border-b border-gray-200 last:border-b-0">
        <button type="button"
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-sm text-gray-900">{title}</span>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>

        {isExpanded && (
          <div className="px-4 py-2 bg-gray-50 space-y-2">
            {values.map(({ value, count }) => (
              <label
                key={value}
                className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(value)}
                  onChange={() => handleFilterToggle(sectionKey, value)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 flex-1">{value}</span>
                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                  {count}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Filters</h3>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="px-4 py-3 border-b border-gray-200 space-y-2">
          {Object.entries(selectedFilters).map(([key, values]) => {
            if (!Array.isArray(values) || values.length === 0) return null;
            return (
              <div key={key} className="space-y-1">
                {values.map(value => (
                  <Badge
                    key={value}
                    variant="secondary"
                    className="inline-flex items-center space-x-1 mr-2 mb-1"
                  >
                    <span>{value}</span>
                    <button type="button"
                      onClick={() => handleFilterToggle(key, value)}
                      className="ml-1 hover:text-gray-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto">
        {renderFilterSection("Type", "types", facets.types)}
        {renderFilterSection("Status", "status", facets.status)}
        {renderFilterSection("Priority", "priority", facets.priority)}
        {renderFilterSection("Tags", "tags", facets.tags)}
      </div>
    </div>
  );
}
